/** Colocated per-locale translations: { locale: { field: value } } */
export type Translations = Record<string, Record<string, string>>;

/**
 * Anything carrying an optional merged translations hash. Deliberately no
 * index signature so interface-typed entities (AppCreatorApp,
 * AppCreatorProperty, …) are assignable.
 */
interface TranslatableEntity {
  translations?: Translations | null;
}

function normalizeLocale(locale?: string | null): string | undefined {
  return locale?.split("_")[0] || undefined;
}

/**
 * Client-side counterpart of Multilingualism::Translatable#localized for
 * entities serialized with a merged translations hash (apps-api format_app):
 * the hash already contains the entity's default locale, so the entity's own
 * field is only the last-resort fallback.
 *
 * Fallback chain (mirrors the backend, issue #4024):
 * requested locale -> network locale -> en, de, fr, es -> entity[field]
 */
export function localizedField(
  entity: TranslatableEntity | null | undefined,
  field: string,
  locale: string = I18n.currentLocale(),
): string {
  if (!entity) return "";

  const ownValue =
    ((entity as Record<string, unknown>)[field] as string | null | undefined) ??
    "";

  if (!Preload.current_network?.config?.multilingualism?.enabled) {
    return ownValue;
  }

  const translations = entity.translations;
  if (!translations) return ownValue;

  const chain = [
    normalizeLocale(locale),
    normalizeLocale(Preload.current_network?.language),
    "en",
    "de",
    "fr",
    "es",
  ].filter((l, i, all): l is string => !!l && all.indexOf(l) === i);

  for (const lang of chain) {
    const translation = translations[lang]?.[field];
    if (translation && translation.trim() !== "") return translation;
  }

  return ownValue;
}
